import React, { useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Box, Button, CircularProgress } from '@mui/material';
import { Image as ImageIcon } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import apiService from '../services/api';
import { RadarPodscopeRankingCard, RANKING_COPY, formatViewsFooterExplainer, type ProgramRow } from '../components/ranking-card';
import {
    RANKING_ASSET_HEIGHT_PX,
    RANKING_ASSET_WIDTH_PX,
} from '../components/ranking-card/rankingAssetDimensions';
import {
    downloadProgramAssetPng,
    useProgramAssetAutoDownload,
} from '../utils/programAssetPngExport';
import AssetPreviewStage from '../components/asset-preview/AssetPreviewStage';
import { waitAssetPreviewPaint } from '../components/asset-preview/assetPreviewViewport';
import { resolveProgramAnalysisTaxonomyFromSearchParams } from '../constants/programAnalysisTaxonomy';

const RANKING_MONTHS_ES = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];

/**
 * Bare-bones render of the ranking card — no sidebar, no buttons, no page chrome.
 * Used for html2canvas PNG export.
 */
const ProgramAnalysisViewsAssetRender: React.FC = () => {
    const [searchParams] = useSearchParams();
    const cardRef = useRef<HTMLDivElement>(null);
    const [isDownloading, setIsDownloading] = useState(false);
    const shouldAutoDownload = searchParams.get('download') === '1';

    const week = searchParams.get('week') ?? undefined;
    const podcastTaxonomy = useMemo(
        () => resolveProgramAnalysisTaxonomyFromSearchParams(searchParams),
        [searchParams],
    );
    const podcastId = searchParams.get('podcastId') ?? undefined;
    const startDate = searchParams.get('startDate') ?? undefined;
    const endDate = searchParams.get('endDate') ?? undefined;

    const { data, isLoading } = useQuery({
        queryKey: ['programs-analysis-views-asset-render', { week, podcastTaxonomy, podcastId, startDate, endDate }],
        queryFn: () =>
            apiService.getProgramsAnalysis(
                week,
                podcastTaxonomy,
                podcastId && podcastId !== 'all' ? podcastId : undefined,
                'avgViewsDay7',
                'desc',
                startDate,
                endDate,
                true
            ),
    });

    const programs = useMemo(() => (data?.programs as ProgramRow[]) ?? [], [data?.programs]);
    const top10ByAvgViews = useMemo(
        () =>
            [...programs]
                .sort(
                    (a, b) =>
                        (b.avgViewsDay7 ?? b.avgViews) - (a.avgViewsDay7 ?? a.avgViews),
                )
                .slice(0, 10),
        [programs],
    );

    const rankingPeriodLabel = useMemo(() => {
        if (week) {
            const start = new Date(week + 'T12:00:00');
            if (!isNaN(start.getTime())) {
                const end = new Date(start);
                end.setDate(end.getDate() + 6);
                const d1 = start.getDate();
                const d2 = end.getDate();
                const month1 = RANKING_MONTHS_ES[start.getMonth()];
                const month2 = RANKING_MONTHS_ES[end.getMonth()];
                if (month1 === month2) {
                    return `Semana del ${d1} al ${d2} de ${month1}`;
                }
                return `Semana del ${d1} de ${month1} al ${d2} de ${month2}`;
            }
        }
        if (startDate && endDate) return `${startDate} – ${endDate}`;
        return 'Período seleccionado';
    }, [week, startDate, endDate]);

    const isReady = top10ByAvgViews.length > 0 && !isLoading;
    const pngFilename = `ranking-views-${rankingPeriodLabel.replace(/\s+/g, '-').replace(/[^a-zA-Z0-9-]/g, '')}.png`;

    useProgramAssetAutoDownload(
        cardRef,
        shouldAutoDownload,
        isReady,
        pngFilename,
        { width: RANKING_ASSET_WIDTH_PX, height: RANKING_ASSET_HEIGHT_PX },
    );

    const handleDownloadPng = async () => {
        setIsDownloading(true);
        await waitAssetPreviewPaint();
        try {
            await downloadProgramAssetPng(cardRef.current, pngFilename, {
                width: RANKING_ASSET_WIDTH_PX,
                height: RANKING_ASSET_HEIGHT_PX,
            });
        } catch (err) {
            console.error('Views asset PNG download failed:', err);
        } finally {
            setIsDownloading(false);
        }
    };

    if (isLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: RANKING_ASSET_WIDTH_PX, height: RANKING_ASSET_HEIGHT_PX, bgcolor: '#1a1a1a' }}>
                <CircularProgress sx={{ color: '#fff' }} />
            </Box>
        );
    }

    if (top10ByAvgViews.length === 0) return null;

    return (
        <AssetPreviewStage
            widthPx={RANKING_ASSET_WIDTH_PX}
            heightPx={RANKING_ASSET_HEIGHT_PX}
            disabled={shouldAutoDownload || isDownloading}
            toolbarExtra={
                process.env.NODE_ENV === 'development' ? (
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={isDownloading ? <CircularProgress size={16} /> : <ImageIcon />}
                        onClick={handleDownloadPng}
                        disabled={isDownloading}
                        sx={{
                            position: 'fixed',
                            top: 8,
                            right: 8,
                            zIndex: 12,
                            bgcolor: 'background.paper',
                        }}
                    >
                        {isDownloading ? 'Generando…' : 'Download PNG'}
                    </Button>
                ) : null
            }
        >
            <Box ref={cardRef}>
                <RadarPodscopeRankingCard
                    programs={top10ByAvgViews}
                    rankingPeriodLabel={rankingPeriodLabel}
                    subheader={RANKING_COPY.views.subheader}
                    footerExplainer={formatViewsFooterExplainer(programs.length)}
                    rankingParam="views"
                    variant="screenshot"
                />
            </Box>
        </AssetPreviewStage>
    );
};

export default ProgramAnalysisViewsAssetRender;
